<template>
  <div class="engagement-phase" :data-qid="'engagement-phase-' + phase">
    <EngagementLockedNotice v-if="phase !== 'acceptance' && !engagement.unlocked"></EngagementLockedNotice>

    <div class="panel">
      <header class="panel__header">
        <div class="profile-compact">
          <div class="logo-container">
            <font-awesome-icon :icon="phase === 'acceptance' ? 'handshake' : 'clipboard-list'" class="icon-svg--sm"/>
          </div>
          <div class="profile-compact__info">
            <div class="profile-compact__top">
              <div class="profile-compact__text">
                {{ $t('projects.engagement.phase_number', { number: phaseNumber }) }} · {{ $t('projects.engagement.phase.' + phase) }}
                <span class="section-compact__secondary">
                  {{ $t('projects.engagement.overview.phase_progress', { done: completedCount, total: includedCount }) }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="profile-compact__actions">
          <span class="engagement-status-pill engagement-status--not_started" v-if="!isManager">
            <font-awesome-icon icon="lock" class="mr-1"/>
            {{ $t('projects.engagement.step_ui.managers_only') }}
          </span>
          <span class="icon-btn"
                v-b-tooltip.hover
                :title="collapsed ? $t('system.view_more') : $t('system.view_less')"
                @click="collapsed = !collapsed"
                :data-qid="'engagement-phase-toggle-' + phase"
          >
            <font-awesome-icon :icon="collapsed ? 'chevron-down' : 'chevron-up'"/>
          </span>
        </div>
      </header>

      <div class="panel__body" v-if="!collapsed">
        <EngagementStep v-for="step in steps"
                        :key="'phase-step-' + step.step"
                        :project="project"
                        :step="step"
                        :editable="canEdit"
                        @changed="$emit('changed', $event)"
                        @media-changed="$emit('media-changed')"
                        @open-tab="$emit('open-tab', $event)"
        ></EngagementStep>
      </div>
    </div>

    <slot name="before-acceptance"></slot>

    <!-- Acceptance gate -->
    <div class="panel engagement-acceptance" v-if="phase === 'acceptance' && engagement.requires_acceptance" data-qid="engagement-acceptance-gate">
      <header class="panel__header">
        <div class="profile-compact">
          <div class="logo-container">
            <font-awesome-icon icon="file-signature" class="icon-svg--sm"/>
          </div>
          <div class="profile-compact__info">
            <div class="profile-compact__top">
              <div class="profile-compact__text">
                {{ $t('projects.engagement.acceptance.title') }}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div class="panel__body">
        <div v-if="engagement.accepted_at" class="engagement-acceptance__state engagement-acceptance__state--accepted">
          <font-awesome-icon icon="check-circle" class="mr-2"/>
          <span v-if="engagement.accepted_by">
            {{ $t('projects.engagement.acceptance.accepted_by', { name: engagement.accepted_by.name, date: $options.filters.formatDate(engagement.accepted_at) }) }}
          </span>
          <span v-else>
            {{ $t('projects.engagement.acceptance.accepted_on', { date: $options.filters.formatDate(engagement.accepted_at) }) }}
          </span>
        </div>
        <div v-else>
          <p class="text-muted mb-2">{{ $t('projects.engagement.acceptance.description') }}</p>
          <div v-if="!allAcceptanceStepsCompleted" class="engagement-acceptance__state">
            <font-awesome-icon icon="exclamation-triangle" class="risk-6 mr-2"/>
            {{ $t('projects.engagement.acceptance.steps_missing') }}
          </div>
        </div>

        <div class="mt-3" v-if="canSignOff">
          <b-button v-if="!engagement.accepted_at"
                    variant="success"
                    :disabled="buttonDisabled || !allAcceptanceStepsCompleted"
                    @click="accept"
                    data-qid="engagement-accept-button"
          >
            <font-awesome-icon icon="file-signature" class="mr-1"/>
            {{ $t('projects.engagement.acceptance.accept_button') }}
          </b-button>
          <b-button v-else
                    variant="secondary"
                    :disabled="buttonDisabled"
                    @click="showWithdrawModal"
                    data-qid="engagement-withdraw-button"
          >
            {{ $t('projects.engagement.acceptance.withdraw_button') }}
          </b-button>
        </div>
      </div>
    </div>

    <b-modal ref="withdraw-acceptance-modal" hide-footer>
      <div class="d-block text-center">
        <h4>{{ $t('projects.engagement.acceptance.withdraw_button') }}</h4>
      </div>
      <h5 class="text-center">
        {{ $t('projects.engagement.acceptance.withdraw_confirm') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3" variant="secondary" @click="$refs['withdraw-acceptance-modal'].hide()">
          {{ $t('projects.create.cancel') }}
        </b-button>
        <b-button class="mt-3 ml-3"
                  variant="danger"
                  :disabled="buttonDisabled"
                  @click="withdraw"
                  data-qid="engagement-withdraw-confirm">
          {{ $t('projects.engagement.acceptance.withdraw_button') }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import EngagementStep from '@/views/Projects/Engagement/EngagementStep.vue';
import EngagementLockedNotice from '@/views/Projects/Engagement/EngagementLockedNotice.vue';
import { PHASE_KEYS, PHASES, canEditPhase, engagementPermissions } from '@/views/Projects/Engagement/engagement.js';

export default {
  name: 'EngagementPhase',
  props: {
    project: {
      type: Object,
      required: true
    },
    engagement: {
      type: Object,
      required: true
    },
    phase: {
      type: String,
      required: true
    }
  },
  components: {
    EngagementStep,
    EngagementLockedNotice
  },
  data: function() {
    return {
      collapsed: false,
      buttonDisabled: false
    }
  },
  computed: {
    phaseNumber() {
      return PHASE_KEYS.indexOf(this.phase) + 1;
    },
    steps() {
      return _.filter(this.engagement.steps || [], step => step.phase === this.phase);
    },
    includedCount() {
      return _.filter(this.steps, 'included').length;
    },
    completedCount() {
      return _.filter(this.steps, step => step.included && step.status === 'completed').length;
    },
    canEdit() {
      return canEditPhase(this.project, this.phase);
    },
    // Every step is completed or not applicable
    isPhaseDone() {
      return _.every(this.steps, step => !step.included || step.status === 'completed');
    },
    isManager() {
      return !!engagementPermissions(this.project).manage;
    },
    canSignOff() {
      return !!engagementPermissions(this.project).sign_off && this.project.status !== 'closed' && this.project.status !== 'completed';
    },
    allAcceptanceStepsCompleted() {
      return _.every(this.engagement.steps || [], step => {
        return !PHASES.acceptance.includes(step.step) || !step.included || step.status === 'completed';
      });
    }
  },
  watch: {
    // Finished phases collapse to their header; the chevron opens them again
    isPhaseDone: {
      immediate: true,
      handler(done) {
        this.collapsed = done;
      }
    }
  },
  methods: {
    accept() {
      let self = this;
      this.buttonDisabled = true;
      piincHttp.post('projects/' + this.project.slug + '/engagement/acceptance').then(function(response) {
        self.buttonDisabled = false;
        self.$toastr('success', self.$t('projects.engagement.acceptance.accept_success'));
        self.$emit('changed', response.data);
      }, function(error) {
        self.buttonDisabled = false;
        self.$toastr('error', error.data && error.data.message ? error.data.message : '');
      });
    },
    showWithdrawModal() {
      this.$refs['withdraw-acceptance-modal'].show();
    },
    withdraw() {
      let self = this;
      this.buttonDisabled = true;
      piincHttp.delete('projects/' + this.project.slug + '/engagement/acceptance').then(function(response) {
        self.buttonDisabled = false;
        self.$refs['withdraw-acceptance-modal'].hide();
        self.$toastr('success', self.$t('projects.engagement.acceptance.withdraw_success'));
        self.$emit('changed', response.data);
      }, function(error) {
        self.buttonDisabled = false;
        self.$toastr('error', error.data && error.data.message ? error.data.message : '');
      });
    }
  }
}
</script>
